interface NodeLike {
  id?: unknown
  children?: unknown[]
}

/**
 * Breadth-first walk to locate the first block whose `.id` matches `id`.
 * BFS so the shallowest match wins if ids are accidentally duplicated.
 * Pure — operates on any `{ children }` tree (a live editor or a plain
 * Slate body), so the client applier and the server validator share it.
 */
export const findBlockPath = (
  root: { children?: unknown[] },
  id: string
): { node: NodeLike; path: number[] } | undefined => {
  const queue: { node: NodeLike; path: number[] }[] = [{ node: root as NodeLike, path: [] }]
  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) {
      continue
    }
    const children = current.node.children
    if (!Array.isArray(children)) {
      continue
    }
    for (let i = 0; i < children.length; i += 1) {
      const child = children[i] as NodeLike | undefined
      if (!child || typeof child !== 'object') {
        continue
      }
      const childPath = [...current.path, i]
      if (child.id === id) {
        return { node: child, path: childPath }
      }
      if (Array.isArray(child.children)) {
        queue.push({ node: child, path: childPath })
      }
    }
  }
  return undefined
}

/**
 * Rewrite an op's absolute paths by prepending `base`. Slate ops carry their
 * target on `path`; `move_node` also carries `newPath`. Ops with neither
 * (e.g. `set_selection`) pass through unchanged.
 */
export const composeOpPath = (op: Record<string, unknown>, base: number[]): Record<string, unknown> => {
  if (base.length === 0) {
    return op
  }
  const out: Record<string, unknown> = { ...op }
  if (Array.isArray(op.path)) {
    out.path = [...base, ...(op.path as number[])]
  }
  if (Array.isArray(op.newPath)) {
    out.newPath = [...base, ...(op.newPath as number[])]
  }
  return out
}

type PathCheck = { ok: true } | { ok: false; reason: string }

/**
 * True when `path` is a legal INSERT location in `tree`: every segment except
 * the last must resolve to an element (a node with a `children` array), and the
 * final index must be within `[0, parent.children.length]`. A path that passes
 * through a leaf/text node (no `children`) is the `[0,0,0,0]` failure class.
 */
const isInsertablePath = (tree: { children?: unknown[] }, path: number[]): PathCheck => {
  let node: NodeLike = tree as NodeLike
  for (let i = 0; i < path.length - 1; i += 1) {
    const children = node.children
    if (!Array.isArray(children)) {
      return { ok: false, reason: 'targets inside a leaf node' }
    }
    const next = children[path[i]] as NodeLike | undefined
    if (!next || typeof next !== 'object') {
      return { ok: false, reason: 'path segment does not exist' }
    }
    node = next
  }
  const children = node.children
  if (!Array.isArray(children)) {
    return { ok: false, reason: 'targets inside a leaf node' }
  }
  const last = path[path.length - 1]
  if (typeof last !== 'number' || last < 0 || last > children.length) {
    return { ok: false, reason: 'index out of range' }
  }
  return { ok: true }
}

export interface SlateOpsLike {
  anchor?: { id: string; subPath?: number[] }
  ops: Record<string, unknown>[]
}

export type SlateOpsValidation = { ok: true } | { ok: false; reason: string }

/**
 * Validate that every op in a slate-ops patch composes to an insertable path
 * in `tree`. Resolves the anchor base (anchor path + subPath) exactly as
 * `applySlateOps` does, then checks each composed `path` AND `newPath`
 * (`move_node` carries both — a bad destination would otherwise throw
 * mid-apply and break the atomicity guarantee). Ops without a `path`
 * (e.g. `set_selection`) are skipped. Single source of truth for "is this
 * slate-ops patch structurally valid against the document".
 *
 * Note: for same-parent moves Slate interprets `newPath` AFTER removal, so a
 * trailing index can shift by one; we validate against the pre-move tree,
 * which permits index === length — deliberately fail-open on that off-by-one
 * rather than false-rejecting a legal move.
 */
export const validateSlateOpsAgainstTree = (
  tree: { children?: unknown[] },
  patch: SlateOpsLike
): SlateOpsValidation => {
  let base: number[] = []
  if (patch.anchor) {
    const found = findBlockPath(tree, patch.anchor.id)
    if (!found) {
      return { ok: false, reason: `anchor block not found: ${patch.anchor.id}` }
    }
    base = [...found.path, ...(patch.anchor.subPath ?? [])]
  }
  for (const op of patch.ops) {
    const composed = composeOpPath(op, base)
    for (const key of ['path', 'newPath'] as const) {
      const candidate = composed[key]
      if (!Array.isArray(candidate)) {
        continue
      }
      const check = isInsertablePath(tree, candidate as number[])
      if (!check.ok) {
        return {
          ok: false,
          reason: `op ${key} ${JSON.stringify(candidate)} ${check.reason} (anchor=${patch.anchor?.id ?? 'root'}, base=${JSON.stringify(base)})`,
        }
      }
    }
  }
  return { ok: true }
}
